import { ESCAPE, LEFT_ARROW, RIGHT_ARROW } from '@angular/cdk/keycodes';
import { HostListener, Injector, ViewChild, Directive } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { FI_DIALOGS_CONTENT_DATA, FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import { FiDialogCloseReason } from '@fi-shared/fi-dialogs/fi-dialogs-close-reason';
import { IFiDialogContentOptions, IFiDialogResultValue } from '@fi-shared/fi-dialogs/fi-dialogs';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { Subscription } from 'rxjs';
import { isDefined } from '@fi-shared/utils';

@Directive()
export abstract class FiDialogsContainerBase<TData, TRes> {
    @ViewChild('noButton', { static: false }) noButton: MatButton;
    @ViewChild('yesButton', { static: false }) yesButton: MatButton;
    isDontShowAgain: boolean = false;

    contentComponentRef: {
        new (...args: any[]): FiDialogsContentBase<TData, TRes>;
    };
    contentInjector: Injector;
    protected contentInstance: FiDialogsContentBase<TData, TRes>;
    FiDialogCloseReason = FiDialogCloseReason;

    constructor(
        protected dialogData: IFiDialogContentOptions<TData, TRes>,
        protected dialogRef: MatDialogRef<FiDialogsContainerBase<TData, TRes>, IFiDialogResultValue<TRes>>,
        injector: Injector,
    ) {
        this.contentInstance = new FiDialogsContentBase<TData, TRes>(null, null, null);
        if (dialogData.contentComponentRef) {
            this.contentComponentRef = dialogData.contentComponentRef;
            this.contentInjector = Injector.create({
                providers: [{ provide: FI_DIALOGS_CONTENT_DATA, useValue: this.dialogData.contentComponentData }],
                parent: injector,
            });
        }

        if (!this.dialogRef.disableClose) {
            // If we should close dialog on Esc and backdrop click then should use custom logic (confirmation support)
            const browserEventsService = injector.get(FiBrowserEventsService);
            this.dialogRef.disableClose = true;
            const eventSubscriptions: Subscription[] = [];
            eventSubscriptions.push(
                this.dialogRef.afterClosed().subscribe(() => {
                    eventSubscriptions.forEach((sub) => sub.unsubscribe());
                }),
            );
            eventSubscriptions.push(
                this.dialogRef.backdropClick().subscribe(($event) => {
                    this.onNoButtonClick($event);
                }),
            );
            eventSubscriptions.push(
                browserEventsService.getWindowEventSubject$('keydown').subscribe(($event: KeyboardEvent) => {
                    if ($event.keyCode === ESCAPE) {
                        this.onNoButtonClick($event);
                    }
                }),
            );

            this.dialogRef.addPanelClass('fi-dialog-open');
        }
    }

    getHeaderText() {
        return this.dialogData.headerText;
    }

    getMessageText(): string {
        return this.dialogData.messageText;
    }

    getFooterCssClass() {
        return this.dialogData.footerCssClass;
    }

    isHeaderDividerShown(): boolean {
        return this.dialogData.isHeaderDividerShown;
    }

    getHeaderDividerClass(): string {
        return this.dialogData.headerDividerClass;
    }

    isFooterDividerShown(): boolean {
        return this.dialogData.isFooterDividerShown;
    }

    isHeaderCloseButtonShown() {
        return this.dialogData.isHeaderCloseButtonShown;
    }

    isNoButtonHidden() {
        return this.contentInstance.isNoButtonHidden() || this.dialogData.isNoButtonHidden || !this.getNoBtnText();
    }

    isYesButtonHidden() {
        return this.contentInstance.isYesButtonHidden() || this.dialogData.isYesButtonHidden || !this.getYesBtnText();
    }

    isNewButtonsLayout(): boolean {
        return this.dialogData.isNewButtonsUI;
    }

    isNoButtonPrimaryColor() {
        return this.dialogData.isNoButtonPrimaryColor;
    }

    getYesBtnText(): string {
        const buttonText: string = this.contentInstance.getYesButtonText() || this.dialogData.yesButtonText || '';
        return this.dialogData.preventUppercaseButtonLabel ? buttonText : buttonText.toUpperCase();
    }

    getNoBtnText(): string {
        const buttonText: string = this.contentInstance.getNoButtonText() || this.dialogData.noButtonText || '';
        return this.dialogData.preventUppercaseButtonLabel ? buttonText : buttonText.toUpperCase();
    }

    isYesButtonDisabled() {
        return this.contentInstance.isYesButtonDisabled();
    }

    onYesButtonClick($event: MouseEvent) {
        this.contentInstance.onYesButtonClick($event);
        if (!$event.defaultPrevented) {
            this.contentInstance.getYesButtonClickPromise().then((result) => {
                if (result?.canClose) {
                    this.closeDialog(FiDialogCloseReason.YesButton);
                }
            });
        }
    }

    onNoButtonClick($event: Event) {
        this.contentInstance.onNoButtonClick($event);
        if (!$event.defaultPrevented) {
            this.contentInstance.getNoButtonClickPromise().then((result) => {
                if (result?.canClose) {
                    this.closeDialog(FiDialogCloseReason.NoButton);
                }
            });
        }
    }

    closeDialog(reason: FiDialogCloseReason) {
        this.dialogRef.removePanelClass('fi-dialog-open');
        this.dialogRef.addPanelClass('fi-dialog-close');
        const result: IFiDialogResultValue<TRes> = {
            value: this.contentInstance.getResult(),
            reason: reason,
            isDontShowAgain: this.isDontShowAgain,
        };
        if (!isDefined(this.dialogData.closeDelayMS)) {
            this.dialogRef.close(result);
        } else {
            setTimeout(() => {
                this.dialogRef.close(result);
            }, this.dialogData.closeDelayMS);
        }
    }

    isDialogTopBorderHidden(): boolean {
        return this.dialogData.hideDialogTopBorder;
    }

    isHeaderHidden() {
        return this.dialogData.hideHeader;
    }

    isFooterHidden() {
        return this.dialogData.hideFooter;
    }

    setContentInstance(contentInstance: FiDialogsContentBase<TData, TRes>) {
        this.contentInstance = contentInstance;
    }

    getCustomActionButtonsTemplate() {
        return this.contentInstance && this.contentInstance.customActionButtonsTemplateRef
            ? this.contentInstance.customActionButtonsTemplateRef
            : undefined;
    }

    isBigSizeTopBorder(): boolean {
        return this.dialogData.isBigSizeTopBorder;
    }

    @HostListener('keydown', ['$event'])
    handleKeyDown(event: KeyboardEvent) {
        var targetTagName = (<any>event.target).tagName.toLowerCase();
        if (targetTagName === 'input' || targetTagName === 'textarea') return;

        if (event.keyCode === LEFT_ARROW && !this.isNoButtonHidden()) {
            this.noButton.focus();
        } else if (event.keyCode === RIGHT_ARROW && !this.isYesButtonHidden() && !this.isYesButtonDisabled()) {
            this.yesButton.focus();
        }
    }
}
